<template>
  <div
    class="upload-media-image upload-media-image--multiple"
    @click="upload"
  >
    <div
      class="upload-media-image__content upload-content"
      :class="{ 'upload-content--loading': loading, 'upload-content--dragover': dragover }"
      @dragenter.prevent
      @dragover.prevent="dragover = droppable"
      @dragleave="dragover = false"
      @drop.prevent="dropped"
    >
      <slot />
    </div>
    <input
      ref="file"
      type="file"
      class="upload-content__file"
      :accept="mimeTypesAccepted"
      multiple
      @change="inputChanged"
    >
  </div>
</template>

<script>
import { values } from 'lodash-es';
import Logger from '@tools/logger/logger';
import requests from '@tools/requests';
import { flashBagError } from '@tools/flashbag';
import { URL_XHR_UPLOAD_MEDIA_IMAGE } from '@tools/routes/routes';
import { trans, transchoice } from '@elements/translation';

export default {
  props: {
    disabled: { type: Boolean, default: false },
    droppable: { type: Boolean, default: false },
  },
  emits: [ 'input', 'loading', 'done' ],
  data() {
    return {
      mimeTypesAccepted: values(window.IMAGE_FORMATS_MIMETYPES).join(', '),
      loading: false,
      fileSizeLimit: 20, // max file size on client-side validation, cannot exceed 50mB
      fileCountLimit: 100,
      dragover: false,
    };
  },
  methods: {
    dropped(event) {
      this.dragover = false;
      if (this.loading || !this.droppable) {
        return;
      }
      event.preventDefault();
      const files = [];
      [ ...event.dataTransfer.items ].forEach((item) => {
        if (item.kind === 'file') {
          files.push(item.getAsFile());
        }
      });
      this.process(files);
    },
    upload() {
      if (this.loading || this.disabled) {
        return;
      }
      this.$refs.file.click();
    },
    inputChanged() {
      const input = this.$refs.file;
      this.process(input.files);
    },
    async process(files) {
      if (this.loading) {
        return;
      }
      const filesToUpload = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file) {
          if (!this.checkSize(file)) {
            this.$refs.file.value = '';
            flashBagError(trans('upload.size_exceeded', { max: this.fileSizeLimit }));
            return;
          }
          filesToUpload.push(file);
        }
      }
      if (filesToUpload.length === 0) {
        return;
      }
      if (filesToUpload.length > this.fileCountLimit) {
        this.$refs.file.value = '';
        flashBagError(trans('upload.count_exceeded', { max: this.fileCountLimit }));
        return;
      }
      this.loading = true;
      this.$emit('loading');
      const promises = [];
      for (let i = 0; i < filesToUpload.length; i++) {
        promises.push(this.uploadFile(filesToUpload[i]));
      }

      let uploadErrorCount = 0;
      const newUuids = [];
      const responses = await Promise.allSettled(promises);
      responses.forEach((response) => {
        if (response.status === 'fulfilled') {
          newUuids.push(response.value);
        } else {
          uploadErrorCount++;
        }
      });

      if (uploadErrorCount) {
        flashBagError(transchoice('media.images.upload_error', { count: uploadErrorCount }));
      }

      this.$refs.file.value = '';
      this.loading = false;
      this.$emit('input', newUuids);
      this.$emit('done');
    },
    checkSize(file) {
      const fileSizeMb = (file.size / (1024 * 1024));
      return fileSizeMb < this.fileSizeLimit;
    },
    uploadFile(file) {
      return new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append('file', file);
        requests.post(URL_XHR_UPLOAD_MEDIA_IMAGE, formData).then((response) => {
          resolve(response.data);
        }).catch((error) => {
          Logger.error(error);
          reject(error);
        });
      });
    },
  },
};
</script>
